/*----------------------------------
- WEBSITE CLICK CONTEXT
----------------------------------*/

/**
 * @docs docs/features/authentication
 * @fix  docs/fixes/2026-09-21-signup-wall-repository-headline.md
 * @rule Facts only, never copy. These helpers decide which page facts travel with a
 *       conversion click. Every string the account screen renders comes from a
 *       conversion catalog, so nothing user-facing may be assembled here.
 * @rule A repository is named only to a visitor who came from GitHub. A page that
 *       carries a repo slug still answers everyone else with the search they are
 *       looking at, and a row click always answers with the row.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type {
    WebsiteConversionCompareContext,
    WebsiteConversionContext,
    WebsiteConversionPageContext,
} from '@uniquedomains/components/conversion/authIntent';
import type { FilterChip } from '@uniquedomains/components/prompt/types';
import type { WebsiteDomainsLandingResponse } from '@/common/types/domains/websiteLanding';

/*----------------------------------
- TYPES
----------------------------------*/

/**
 * The slice of the landing payload a click context is built from. Narrowed on purpose:
 * the context travels inside a URL, so depending on the whole response would invite
 * fields the builder can only drop.
 */
export type SearchConversionSource = Pick<WebsiteDomainsLandingResponse, 'githubRepoSlug' | 'humanized'> & {
    stats?: Pick<WebsiteDomainsLandingResponse['stats'], 'count' | 'countPrecision' | 'medianAsk'> | null;
};

export type SearchConversionContextInput = {
    search: SearchConversionSource | null | undefined;
    filterChips?: readonly FilterChip[];
    heroTitle?: string | null;
    /** The visit arrived through a GitHub dataset link (`?github_intent=`). */
    cameFromGithub: boolean;
    /** The row the visitor opened, on a row click only. */
    domain?: string | null;
};

/*----------------------------------
- HELPERS
----------------------------------*/

const toContextMetric = (value: number | null | undefined): number | undefined => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
    /* Rounded because the shared normalizer keeps safe integers only, and a median ask
       arrives with cents: 2399.5 would otherwise drop out of the headline entirely. */
    return Math.round(value);
};

const toContextChips = (chips: readonly FilterChip[] | undefined): string[] | undefined => {
    const formatted: string[] = [];
    for (const chip of chips ?? []) {
        const label = chip.label.trim();
        const value = chip.value.trim();
        /* A half-filled chip would render as a dangling "Sort:" pill, so it is dropped
           rather than shown as a filter the visitor never set. */
        if (!label || !value) continue;
        formatted.push(`${label}: ${value}`);
    }

    /* Neither capped nor deduped here: both rules live in the shared normalizer, so the
       link and the intent parsed back out of it cannot disagree on which chips survived. */
    return formatted.length ? formatted : undefined;
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const buildSearchConversionContext = ({
    search,
    filterChips,
    heroTitle,
    cameFromGithub,
    domain,
}: SearchConversionContextInput): WebsiteConversionContext | undefined => {
    const title = search?.humanized?.trim() || heroTitle?.trim() || undefined;
    const count = toContextMetric(search?.stats?.count);
    /* The wall prints the count the page printed: a sampled one keeps its tilde (ADR-0034). */
    const countPrecision = count !== undefined && search?.stats?.countPrecision === 'sampled' ? 'sampled' : undefined;
    const medianAsk = toContextMetric(search?.stats?.medianAsk);
    const repo = search?.githubRepoSlug?.trim();
    const clickedDomain = domain?.trim() || undefined;

    /*
     * The repository headline is for a visitor who came from that repository. The slug is a
     * fact about the page, not about the visit: on 2026-09-20 an investor referred by ChatGPT
     * was asked to "Turn uniquedomains/oneword-domains into a Radar", a repo they had never
     * seen, because the slug used to win on its own. A row click is excluded as well, since
     * the visitor then named something more specific than the dataset.
     * See docs/fixes/2026-09-21-signup-wall-repository-headline.md.
     */
    if (repo && cameFromGithub && !clickedDomain) {
        return { kind: 'dataset', repo, title, count, ...(countPrecision ? { countPrecision } : {}), medianAsk };
    }

    /* The search headline quotes the query back to the visitor. With nothing to quote the
       click carries no context at all, rather than a headline built around an empty search. */
    if (!title) return undefined;

    return {
        kind: 'search',
        title,
        count,
        ...(countPrecision ? { countPrecision } : {}),
        medianAsk,
        chips: toContextChips(filterChips),
        /* Validated and lowercased by the shared normalizer, like every other context field,
           so the link and the intent parsed back out of it name the same row. */
        ...(clickedDomain ? { domain: clickedDomain } : {}),
    };
};

export const buildCompareConversionContext = (slug?: string | null): WebsiteConversionCompareContext => ({
    kind: 'compare',
    slug: slug?.trim() || undefined,
});

/**
 * A click on a page that returns to a Product view. `tld` is the ending the click named, when it
 * named one: the handoff turns it into the brief's `tld` rule (`resolveSearchProofFilters`) while
 * `returnTo` keeps the view a signed-in member asked for.
 */
export const buildProductPageConversionContext = (
    title?: string | null,
    { tld }: { tld?: string | null } = {},
): WebsiteConversionPageContext => {
    const ending = tld?.trim() || undefined;
    return { kind: 'page', title: title?.trim() || undefined, ...(ending ? { tld: ending } : {}) };
};
